// src/client/game/core/GameModel.ts

import { Snake } from "./Snake";
import { TailModel } from "./tail/TailModel";
import { AmmoClip } from "./ammo/AmmoClip";
import { DeadTailOnlyPolicy } from "./ammo/ConsumePolicy";
import { SegmentSkin } from "./tail/SegmentSkin";
import type { Charge, Direction as ChargeDirection } from "./charges/Charge";
import {
  FoodKind,
  type Direction,
  type FoodInstance,
  type GridPos,
} from "./types";
import { foodRegistry } from "./foods";
import { shootLaserForGame, type LaserAim } from "./Laser";
import { FoodSystem, type FoodSpawnTuning } from "./FoodSystem";
import { EnemySystem } from "./EnemySystem";

export interface TickResult {
  moved: boolean;
  died: boolean;
  ateFoods: FoodInstance[];
}

export class GameModel {
  readonly width: number;
  readonly height: number;

  snake: Snake;
  /** Engine-agnostic tail metadata mirror (stores per-segment metadata like charges). */
  readonly tailModel: TailModel<Charge> = new TailModel();
  /** Read/consume view over tail-stored charges. */
  private readonly ammoClip = new AmmoClip<Charge>(this.tailModel);
  /** Only allow firing charges in the last N tail segments ("dead tail"). */
  private readonly chargeConsumePolicy = new DeadTailOnlyPolicy(3);
  /** AI-controlled enemy snakes (rendering + laser target). */
  enemySnakes: Snake[] = [];

  foods: FoodInstance[] = [];
  obstacles: Set<string> = new Set();
  isGameOver = false;

  /** How long the current run has been alive (ms). */
  timeAliveMs = 0;

  // Spawning / pacing state (movement only; food timing lives in FoodSystem)
  rng: () => number;
  elapsedSinceMoveMs = 0;

  // Score thresholds for special foods
  nextBlueScoreAt = 800; // blue every 800 points
  nextLaserScoreAt = 500;
  nextStarScoreAt = 1500;
  nextIcyScoreAt = 2500;

  // For the view to draw a beam
  lastLaserPath: GridPos[] = [];

  // Enemy laser beams fired this tick (for renderer)
  enemyLaserShots: { origin: GridPos; path: GridPos[] }[] = [];

  // Stats
  /** Number of enemy snakes killed this run. */
  enemiesKilled = 0;

  // Systems
  private foodSystem!: FoodSystem;
  private enemySystem!: EnemySystem;

  constructor(width: number, height: number, rng: () => number = Math.random) {
    this.width = width;
    this.height = height;
    this.rng = rng;

    this.initRunState();
  }

  /** Current score is just the snake's score. */
  get score(): number {
    return this.snake.score;
  }

  get moveIntervalMs(): number {
    return this.snake.getSpeedMs();
  }

  get laserLevel(): number {
    return this.snake.laserLevel ?? 0;
  }

  get hasLaser(): boolean {
    return (this.snake.laserLevel ?? 0) > 0;
  }

  /**
   * Main game loop tick.
   * Advances timers, maybe moves the snake, resolves collisions/food, then enemies.
   */
  tick(deltaMs: number): TickResult {
    if (this.isGameOver) {
      return { moved: false, died: false, ateFoods: [] };
    }

    this.advanceTime(deltaMs);

    if (!this.shouldMoveSnake()) {
      return { moved: false, died: false, ateFoods: [] };
    }

    // Clear per-tick enemy laser shots (rendered by the scene)
    this.enemyLaserShots = [];

    const { newHead, removedTail, hitSelf, hitObstacle } = this.snake.step(
      this.width,
      this.height,
      this.obstacles
    );
    void removedTail;

    // Mirror movement into the tail model (metadata unused for now)
    this.tailModel.step(newHead);

    let died = hitSelf || hitObstacle;
    let ateFoods: FoodInstance[] = [];

    // Check collision with enemy snakes (any segment)
    if (!died) {
      for (const enemy of this.enemySnakes) {
        if (
          enemy.segments.some(
            (seg) => seg.x === newHead.x && seg.y === newHead.y
          )
        ) {
          died = true;
          break;
        }
      }
    }

    if (!died) {
      ateFoods = this.resolveFoodAt(newHead);
      // Reconcile tail length after any food effects (grow/shrink).
      this.reconcileTailModelLength();
      // Only update enemies if player is still alive this tick.
      this.enemySystem.tick();

      // Enemies (hunters) can kill/cut you with lasers.
      if (this.isGameOver) {
        died = true;
      }
    } else {
      this.isGameOver = true;
    }

    return { moved: true, died, ateFoods };
  }

  setDirection(dir: Direction): void {
    this.snake.setDirection(dir);
  }

  /**
   * Reset the game to a fresh run.
   */
  reset(): void {
    this.isGameOver = false;
    this.initRunState();
  }

  /**
   * Spawn a specific food kind somewhere on the board.
   * Delegates to FoodSystem so external callers don't need to know about it.
   */
  spawnFood(kind: FoodKind): void {
    this.foodSystem.spawnFood(kind);
  }

  /**
   * Configure food pacing + mix (used by level configs).
   */
  setFoodSpawnTuning(tuning: FoodSpawnTuning): void {
    this.foodSystem.setSpawnTuning(tuning);
  }

  /**
   * Clear foods and restart food timers (useful when changing levels).
   */
  resetFoods(): void {
    this.foods = [];
    this.foodSystem.reset();
  }

  /**
   * Apply a new base move interval (ms per cell at speedMultiplier=1).
   */
  setBaseSpeedMs(baseSpeedMs: number): void {
    this.snake.baseSpeedMs = baseSpeedMs;
  }


  /** Spawn a single enemy snake (used by blue food). */
  spawnEnemySnake(): void {
    this.enemySystem.spawnEnemySnake();
  }

  /** Spawn a single hunter enemy snake (late-game). */
  spawnHunterSnake(): void {
    this.enemySystem.spawnHunterSnake();
  }

  /** Spawn the level boss snake (late-campaign). */
  spawnBossSnake(): void {
    this.enemySystem.spawnBossSnake();
  }

  /** Boss phase hook (1 = calm, 2 = enraged). */
  setBossPhase(phase: 1 | 2): void {
    this.enemySystem.setBossPhase(phase);
  }

  /** Fire one eligible tail charge (if any). Returns true only if something actually fired. */
  fire(): boolean {
    this.lastLaserPath = [];

    // No laser unlocked yet.
    if (!this.hasLaser) return false;

    // Consume a charge from the "dead tail" zone.
    const ref = this.ammoClip.consume(this.chargeConsumePolicy, SegmentSkin.Spent);
    if (!ref) return false;

    // Execute the consumed charge.
    const dir = this.toChargeDirection(this.snake.direction);
    ref.charge.fire({
      direction: dir,
      shootLaser: (d: ChargeDirection) => {
        this.shootLaser(d);
      },
    });

    return true;
  }

  private toChargeDirection(dir: Direction): ChargeDirection {
    if (dir.dx === 1) return "right";
    if (dir.dx === -1) return "left";
    if (dir.dy === 1) return "down";
    return "up";
  }

  private chargeDirToAim(dir: ChargeDirection): LaserAim {
    switch (dir) {
      case "right": return { dx: 1, dy: 0 };
      case "left": return { dx: -1, dy: 0 };
      case "down": return { dx: 0, dy: 1 };
      case "up": default: return { dx: 0, dy: -1 };
    }
  }


  /**
   * Laser levels:
   * - L1: classic (cuts your snake if you hit your own body)
   * - L2: stops at first hit and clears obstacles along the beam path
   * - L3: same as L2 plus a small explosion
   *
   * Implementation is delegated to Laser.ts, which also knows how to hit enemy snakes.
   */
  shootLaser(direction?: ChargeDirection): void {
    const aim: LaserAim | undefined = direction ? this.chargeDirToAim(direction) : undefined;
    shootLaserForGame(this, aim);
    // Laser can cut the body; keep tail mirror in sync.
    this.reconcileTailModelLength();
  }

  /** Called whenever an enemy snake is killed. Awards bonus score. */
  onEnemyKilled(): void {
    this.enemiesKilled += 1;
    this.snake.score += 100;
  }

  /** Called when a hunter laser hits the player snake. */
  onEnemyLaserHitPlayer(hitIndex: number): void {
    // Headshot: instant death.
    if (hitIndex <= 0) {
      this.isGameOver = true;
      return;
    }

    // Body hit: cut the tail after the hit point and convert cut segments to obstacles.
    if (hitIndex >= this.snake.segments.length - 1) return;

    const newBody = this.snake.segments.slice(0, hitIndex + 1);
    const cutTail = this.snake.segments.slice(hitIndex + 1);

    for (const seg of cutTail) {
      this.obstacles.add(`${seg.x},${seg.y}`);
    }

    this.snake.segments = newBody;
    this.reconcileTailModelLength();
  }

  // ---- Internal helpers ----

  private initRunState(): void {
    // Core scalar state
    this.foods = [];
    this.obstacles.clear();
    this.enemySnakes = [];
    this.timeAliveMs = 0;
    this.elapsedSinceMoveMs = 0;

    // Reset score thresholds
    this.nextBlueScoreAt = 800;
    this.nextLaserScoreAt = 500;
    this.nextStarScoreAt = 1500;
    this.nextIcyScoreAt = 2500;

    this.lastLaserPath = [];
    this.enemyLaserShots = [];
    this.enemiesKilled = 0;

    // Snake start position in the middle of the grid
    const start: GridPos = {
      x: Math.floor(this.width / 2),
      y: Math.floor(this.height / 2),
    };
    this.snake = new Snake(start, 10, { dx: 1, dy: 0 }, 500);


    // Tail model mirrors the snake body (metadata unused for now)
    this.tailModel.setFromBody(this.snake.segments);

    // Initialize or reset systems

    // Food system: ensure base food exists
    if (!this.foodSystem) {
      this.foodSystem = new FoodSystem(this, this.rng);
    }
    this.foodSystem.reset();

    // Enemy system: always reset so we start with ZERO enemies each run
    if (!this.enemySystem) {
      this.enemySystem = new EnemySystem(this, this.rng);
    }
    this.enemySystem.reset();
  }

  private advanceTime(deltaMs: number): void {
    // Run timer (counts even between moves).
    this.timeAliveMs += deltaMs;
    this.elapsedSinceMoveMs += deltaMs;

    // Food spawning / pacing lives in FoodSystem
    this.foodSystem.tick(deltaMs);
  }

  private shouldMoveSnake(): boolean {
    if (this.elapsedSinceMoveMs < this.moveIntervalMs) {
      return false;
    }
    this.elapsedSinceMoveMs -= this.moveIntervalMs;
    return true;
  }



  /**
   * Keep TailModel length in sync with Snake length while preserving any per-segment metadata.
   * This is currently "inactive" (metadata isn't used yet), but it keeps future steps small.
   */
  private reconcileTailModelLength(): void {
    const body = this.snake.segments;
    const bodyLen = body.length;

    // If tail mirror is empty or clearly out of sync, do a hard reset.
    const head = body[0];
    const mirrorHead = this.tailModel.getSegment(0)?.pos;
    if (
      !head ||
      this.tailModel.length === 0 ||
      !mirrorHead ||
      mirrorHead.x !== head.x ||
      mirrorHead.y !== head.y
    ) {
      this.tailModel.setFromBody(body);
      return;
    }

    const delta = bodyLen - this.tailModel.length;
    if (delta > 0) {
      for (let i = 0; i < delta; i++) this.tailModel.growAtTail();
    } else if (delta < 0) {
      this.tailModel.truncate(bodyLen);
    }
  }

  private resolveFoodAt(newHead: GridPos): FoodInstance[] {
    const ateFoods: FoodInstance[] = [];
    const remainingFoods: FoodInstance[] = [];

    for (const food of this.foods) {
      if (food.x === newHead.x && food.y === newHead.y) {
        ateFoods.push(food);
      } else {
        remainingFoods.push(food);
      }
    }

    this.foods = remainingFoods;

    if (ateFoods.length > 0) {
      for (const food of ateFoods) {
        const def = foodRegistry[food.id];
        def.apply(this);
      }
    }

    return ateFoods;
  }
}